<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
});

const { t } = useI18n();

const agents = computed(() => props.data?.agents || []);

// Fechados antes do rastreio de vendedor (sem closed_by): mostrados como
// linha própria para o total bater — atribuição não se inventa.
const unattributed = computed(() => props.data?.unattributed || null);
const hasUnattributed = computed(
  () => (unattributed.value?.won || 0) + (unattributed.value?.lost || 0) > 0
);

const maxWonValue = computed(() => {
  if (!agents.value.length) return 1;
  return Math.max(...agents.value.map(a => a.won_value || 0), 1);
});

const getBarWidth = value => {
  const percentage = (value / maxWonValue.value) * 100;
  return `${Math.max(percentage, 5)}%`;
};
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-6">
      {{ t('DEAL_REPORTS.AGENTS.TITLE') }}
    </h3>

    <div v-if="agents.length" class="space-y-4">
      <div
        v-for="agent in agents"
        :key="agent.id"
        class="flex items-center gap-4"
      >
        <!-- Avatar -->
        <Avatar
          :src="agent.avatar_url"
          :name="agent.name"
          :size="32"
          rounded-full
        />

        <!-- Info -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between mb-1">
            <span
              class="text-sm font-medium text-slate-900 dark:text-white truncate"
            >
              {{ agent.name }}
            </span>
            <span class="text-sm text-slate-500 dark:text-slate-400 ml-2">
              {{ formatCurrency(agent.won_value) }}
            </span>
          </div>

          <!-- Progress bar -->
          <div
            class="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden"
          >
            <div
              class="h-full bg-green-500 rounded-full transition-all duration-300"
              :style="{ width: getBarWidth(agent.won_value) }"
            />
          </div>

          <!-- Stats -->
          <div
            class="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400"
          >
            <span>
              {{ agent.total_deals }} {{ t('DEAL_REPORTS.AGENTS.DEALS') }}
            </span>
            <span class="text-green-600 dark:text-green-400">
              {{ agent.won }} {{ t('DEAL_REPORTS.AGENTS.WON') }}
            </span>
            <span class="text-red-600 dark:text-red-400">
              {{ agent.lost }} {{ t('DEAL_REPORTS.AGENTS.LOST') }}
            </span>
            <span>
              {{ agent.conversion_rate }}%
              {{ t('DEAL_REPORTS.AGENTS.CONVERSION') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!hasUnattributed"
      class="h-48 flex items-center justify-center"
    >
      <p class="text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.NO_DATA') }}
      </p>
    </div>

    <!-- Fechados sem atribuição (histórico pré-rastreio) -->
    <div
      v-if="hasUnattributed"
      class="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-500 dark:text-slate-400"
    >
      <span>{{ t('DEAL_REPORTS.AGENTS.UNATTRIBUTED') }}</span>
      <span>
        {{ unattributed.won }} {{ t('DEAL_REPORTS.AGENTS.WON') }} ·
        {{ unattributed.lost }} {{ t('DEAL_REPORTS.AGENTS.LOST') }} ·
        {{ formatCurrency(unattributed.won_value) }}
      </span>
    </div>
  </div>
</template>
